import { Component, OnInit, ViewChild, ElementRef, Input, EventEmitter, Output } from '@angular/core';
import { trigger, state, style, transition, animate, query, stagger } from '@angular/animations';
import { CustomerService } from '../../services/customer.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PrintService } from '../../services/print.service';

import { buildDashboardThemeVars } from '../../shared/dashboard-widget-theme';
@Component({
	selector: 'app-customers-online',
	templateUrl: './customers-online.component.html',
	styleUrls: ['./customers-online.component.scss'],
	animations: [
		trigger('flipState', [
			state('active', style({
				transform: 'rotate3d(0, 1, 0, 180deg)',
			})),
			state('inactive', style({
				transform: 'rotateY(0)'
			})),
			state('orderlookup', style({
				transform: 'rotateY(179deg)'
			})),
			transition('active => inactive', animate('500ms ease-out')),
			transition('inactive => active', animate('500ms ease-in')),
			transition('inactive => orderlookup', animate('500ms ease-out')),
			transition('orderlookup => inactive', animate('500ms ease-in')),
		]),
		trigger('openClose', [
			state(':enter', style({ height: '*' })),
			state(':leave', style({ height: '0px' })),
			transition('false <=> true', animate(500))
		]),
		trigger('filterAnimation', [
			transition(':enter, * => 0, * => -1', []),
			transition(':increment', [
				query(':enter', [
					style({ opacity: 0, width: 0 }),
					stagger(50, [
						animate('300ms ease-out', style({ opacity: 1, width: '*' })),
					]),
				], { optional: true })
			]),
			transition(':decrement', [
				query(':leave', [
					stagger(50, [
						animate('300ms ease-out', style({ opacity: 0, width: 0 })),
					]),
				])
			]),
		]),
		trigger('grow', [ // Note the trigger name
			transition(':enter', [
				// :enter is alias to 'void => *'
				style({ height: '0', width: '0' }),
				animate(200, style({ height: '*', width: '*' })),
				animate('200ms', style({ opacity: 1 })),
			]),
			transition(':leave', [
				// :leave is alias to '* => void'
				animate(100, style({ height: 0, width: 0 })),
				animate('100ms', style({ opacity: 0 }))
			])
		]),
	]
})

export class CustomersOnlineComponent implements OnInit {

	get themeVars(): Record<string, string> {
		return buildDashboardThemeVars('cw', 'primary');
	}

	@Input() flip = 'inactive';
	@Output() viewData = new EventEmitter<any>();
	@ViewChild('print_table') printTable: ElementRef;

	viewing: any = false;
	data: any = false;
	title = 'Customers Online';
	searchTerm = '';
	filteredRows: any[] = [];
	exporting = false;

	constructor(
		private customerService: CustomerService,
		private globalSearchService: GlobalSearchService,
		private printService: PrintService
	) {}

	ngOnInit(): void {
		this.customerService.getCurrentOnline().subscribe((results: any) => {
			this.data = Array.isArray(results) ? results : [];
			this.applySearch();
		});
	}

	get displayData(): any[] {
		return this.filteredRows;
	}

	applySearch(): void {
		const term = (this.searchTerm || '').trim().toLowerCase();
		if (!term) {
			this.filteredRows = this.data ? [...this.data] : [];
			return;
		}
		this.filteredRows = (this.data || []).filter((row: any) => {
			const ip = (row.ip || '').toString().toLowerCase();
			const custID = (row.custID ?? row.customer_id ?? '').toString().toLowerCase();
			const branch = (row.branchcode || '').toString().toLowerCase();
			const name = (row.brname || '').toString().toLowerCase();
			const addr = (row.braddress1 || '').toString().toLowerCase();
			const city = (row.braddress3 || '').toString().toLowerCase();
			const region = (row.braddress4 || '').toString().toLowerCase();
			const postal = (row.braddress5 || '').toString().toLowerCase();
			const phone = (row.phoneno || '').toString().toLowerCase();
			const cart = (row.cart_id ?? '').toString().toLowerCase();
			const qty = (row.quantity ?? '').toString().toLowerCase();
			const date = (row.custDate || '').toString().toLowerCase();
			const url = (row.url || '').toString().toLowerCase();
			return ip.includes(term) || custID.includes(term) || branch.includes(term) || name.includes(term) ||
				addr.includes(term) || city.includes(term) || region.includes(term) || postal.includes(term) ||
				phone.includes(term) || cart.includes(term) || qty.includes(term) || date.includes(term) || url.includes(term);
		});
	}

	toggleFlip(data: any, title: string): void {
		if (!data) {
			this.flip = 'inactive';
		} else {
			this.flip = (this.flip === 'active') ? 'inactive' : 'active';
		}
		if (this.flip === 'active') {
			this.viewing = data;
			this.title = title;
			this.applySearch();
			this.viewData.emit({ data, title: this.title });
		} else {
			this.viewData.emit(false);
		}
	}

	exportXlsx(): void {
		const rows = (this.displayData && this.displayData.length) ? this.displayData : (this.data || []);
		if (!rows.length) {
			return;
		}
		const toExport = rows.map((d: any) => ({
			IP: d.ip ?? '',
			'Customer#': d.custID ?? d.customer_id ?? '',
			'Branch#': d.branchcode ?? '',
			Name: d.brname ?? '',
			Address: d.braddress1 ?? '',
			City: d.braddress3 ?? '',
			Region: d.braddress4 ?? '',
			'Postal#': d.braddress5 ?? '',
			'Phone#': d.phoneno ?? '',
			'Cart#': d.cart_id ?? '',
			Qty: d.quantity ?? '',
			'Last seen': d.custDate ?? '',
			URL: d.url ?? '',
		}));
		this.globalSearchService.exportJsonToExcel(toExport, 'CustomersOnline_' + new Date().toISOString().slice(0, 10));
	}

	exportPdf(): void {
		if (!this.printTable?.nativeElement || this.exporting) return;
		this.exporting = true;
		const encoded = this.globalSearchService.base64encode(this.printTable.nativeElement.innerHTML);
		const filename = 'CustomersOnline_' + new Date().toISOString().slice(0, 10);
		this.printService.pdf({
			content: encoded,
			filename,
			title: 'Customers Online',
			subtitle: 'Exported ' + new Date().toLocaleString()
		}).subscribe({
			next: (result: any) => {
				this.globalSearchService.downloadPdf(result.content, filename);
				this.exporting = false;
			},
			error: () => { this.exporting = false; }
		});
	}
}
